/**
 * File: accountingService.ts
 * Path: apps/admin/src/shared/services/accountingService.ts
 * Description: Every read the admin makes on the books, and the one way it
 * changes them: the `accounting` callable. Firestore rules refuse any write
 * from here, so a journal can only be posted after the server has checked
 * it, numbered it and logged it.
 *
 * Firestore comes from a getter so the rules tests run these exact reads
 * against the emulator (tests/rules/accounting.rules.test.ts).
 */

import {
  ACCOUNTING_COLLECTIONS,
  ACCOUNTING_FUNCTION,
  BANK_COLLECTIONS,
  toBankLineRecord,
  toBankRule,
  toCustomBankRule,
  toReconciliationRecord,
  isCustomRuleDoc,
  toBankStatementRecord,
  emptyTaxYear,
  BOOKS_EVENTS_LIMIT,
  byJournalOrder,
  db,
  FunctionsService,
  booksFilePath,
  BO_COLLECTION,
  CIPC_COLLECTION,
  CIPC_DETAILS_DOC,
  COMPANY_PROFILE_ID,
  emptyCompanyDetails,
  EQUIPMENT_DOC,
  FILINGS_COLLECTION,
  TAGS_DOC,
  TAX_RECORDS_COLLECTION,
  TAX_YEAR_COLLECTION,
  toBooksEvent,
  toBooksProfile,
  toJournal,
  toLedgerAccount,
  toBoDeclaration,
  toCompanyDetails,
  toEquipment,
  toFiling,
  toTaxTags,
  toTaxYear,
  type AccountType,
  type Allocation,
  type BankLineRecord,
  type BankRule,
  type CustomBankRule,
  type ReconciliationRecord,
  type BankStatementRecord,
  type BankStatement,
  type BooksEvent,
  type BooksProfile,
  type Journal,
  type JournalDocument,
  type JournalLine,
  type LedgerAccount,
  type ProfileKind,
  type BoDeclaration,
  type BoDocument,
  type CompanyDetails,
  type FilingRecord,
  type EquipmentItem,
  type ImportEntry,
  type ProvisionalPeriod,
  type TaxRecords,
  type TaxTag,
  type TaxYearInputs,
  type BudgetPeriod,
} from "@idk/config";
import { StorageService } from "./storageService";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  type Firestore,
} from "firebase/firestore";

const PROFILES = ACCOUNTING_COLLECTIONS.profiles;

/** What the callable accepts (apps/server/src/features/accounting/accountingValidator.ts). */
export type BooksCommand =
  | { action: "setupProfile"; kind: ProfileKind; booksStart?: string; yearEndMonth?: number }
  | ({ action: "saveTaxYear"; profileId: string } & Omit<TaxYearInputs, "provisional" | "provisionalJournals" | "estimates" | "updatedAt" | "updatedBy">)
  | { action: "saveEquipment"; profileId: string; items: EquipmentItem[] }
  | { action: "saveTaxTags"; profileId: string; accounts: Record<string, TaxTag>; entries: Record<string, TaxTag> }
  | {
      action: "payProvisional";
      profileId: string;
      yearEnd: string;
      period: ProvisionalPeriod;
      amountCents: number;
      paidOn: string;
      estimateCents: number;
      bankAccountId: string | null;
    }
  | { action: "undoProvisional"; profileId: string; yearEnd: string; period: ProvisionalPeriod }
  | ({ action: "saveCompanyDetails"; profileId: string } & Omit<CompanyDetails, "boVersionCount" | "updatedAt" | "updatedBy">)
  | ({ action: "saveBeneficialOwnership"; profileId: string } & Omit<BoDeclaration, "id" | "version" | "savedAt" | "savedBy">)
  | {
      action: "markFiling";
      profileId: string;
      obligationId: string;
      filedOn: string | null;
      dueOnOverride: string | null;
      note: string;
    }
  | {
      action: "saveAccount";
      profileId: string;
      accountId?: string;
      code: string;
      name: string;
      type: AccountType;
      taxLine: string | null;
      money?: boolean;
      archived: boolean;
    }
  | {
      action: "postJournal";
      profileId: string;
      date: string;
      narration: string;
      lines: JournalLine[];
      documents?: JournalDocument[];
    }
  | { action: "attachDocument"; profileId: string; journalId: string; document: JournalDocument }
  | { action: "reverseJournal"; profileId: string; journalId: string; date: string; reason: string }
  | { action: "bankPdfPassword"; profileId: string }
  | { action: "moveBooksStart"; profileId: string; booksStart: string }
  | { action: "setLock"; profileId: string; lockedThrough: string | null; reason: string }
  | { action: "importOpening"; profileId: string; lines: JournalLine[] }
  | {
      action: "importHistory";
      profileId: string;
      entries: Pick<ImportEntry, "ref" | "date" | "narration" | "party" | "project" | "lines">[];
    }
  | {
      action: "importBankStatement";
      profileId: string;
      fileName: string;
      bank: BankStatement["bank"];
      format: BankStatement["format"];
      from: string | null;
      to: string | null;
      openingCents: number | null;
      closingCents: number | null;
      lines: BankStatement["lines"];
      document?: JournalDocument;
    }
  | {
      action: "postBankLine";
      profileId: string;
      lineId: string;
      accountId?: string;
      contract?: { contractId: string; reference: string };
      /** Money in that pays invoices: the split Liam confirmed, the rest held as the client's credit. */
      receipt?: { allocations: Allocation[]; creditCents: number; creditReference: string; party: string; clientId: string | null };
      split?: { accountId: string; amountCents: number }[];
      memo?: string;
      name?: string;
    }
  | { action: "renameBankLine"; profileId: string; lineId: string; name: string }
  | { action: "postBankGroup"; profileId: string; lineIds: string[]; accountId: string; name: string }
  | { action: "postBankLines"; profileId: string; items: { lineId: string; accountId: string; name?: string }[] }
  | { action: "setBankLinesOff"; profileId: string; lineIds: string[]; off: boolean }
  | { action: "undoBankImport"; profileId: string; statementId: string }
  | {
      action: "reconcilePeriod";
      profileId: string;
      statementId: string | null;
      from: string;
      to: string;
      closingCents: number;
      journalIds: string[];
    }
  | { action: "undoReconciliation"; profileId: string; reconciliationId: string }
  | {
      action: "saveBankRule";
      profileId: string;
      ruleId?: string;
      contains: string;
      direction: "in" | "out" | "any";
      accountId: string;
      name?: string;
      auto: boolean;
    }
  | { action: "changeBankRules"; profileId: string; deleteId?: string; orderedIds?: string[] }
  | { action: "linkClients"; profileId: string; apply: boolean; createJobs?: boolean };

export const createAccountingRepo = (
  getDb: () => Firestore,
  call: (command: BooksCommand) => Promise<unknown> = async () => {
    throw new Error("No accounting callable configured.");
  },
) => {
  const section = (profileId: string, name: string) =>
    collection(getDb(), PROFILES, profileId, name);

  return {
    /** The profile, or null when those books haven't been set up yet. */
    getProfile: async (profileId: string): Promise<BooksProfile | null> => {
      const snap = await getDoc(doc(getDb(), PROFILES, profileId));
      return snap.exists() ? toBooksProfile(snap.id, snap.data()) : null;
    },

    listAccounts: async (profileId: string): Promise<LedgerAccount[]> => {
      const snap = await getDocs(section(profileId, ACCOUNTING_COLLECTIONS.accounts));
      return snap.docs
        .flatMap((d) => {
          const account = toLedgerAccount(d.id, d.data());
          return account ? [account] : [];
        })
        .sort((a, b) => a.code.localeCompare(b.code, "en", { numeric: true }));
    },

    /**
     * Every journal, oldest first; the reports work from the whole set in the
     * browser. Since the Zoho history and bank imports this is the biggest
     * read in the admin, so it is cached (useBooks) and a bank-line change
     * adds the one new journal instead of reading them all again.
     */
    listJournals: async (profileId: string): Promise<Journal[]> => {
      const snap = await getDocs(section(profileId, ACCOUNTING_COLLECTIONS.journals));
      return snap.docs
        .flatMap((d) => {
          const journal = toJournal(d.id, d.data());
          return journal ? [journal] : [];
        })
        .sort(byJournalOrder);
    },

    /** Every bank line saved from statements, oldest first. */
    listBankLines: async (profileId: string): Promise<BankLineRecord[]> => {
      const snap = await getDocs(section(profileId, BANK_COLLECTIONS.lines));
      return snap.docs
        .flatMap((d) => {
          const record = toBankLineRecord(d.id, d.data());
          return record ? [record] : [];
        })
        .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
    },

    /** The statements uploaded, newest first. */
    listBankStatements: async (profileId: string): Promise<BankStatementRecord[]> => {
      const snap = await getDocs(section(profileId, BANK_COLLECTIONS.statements));
      return snap.docs
        .map((d) => toBankStatementRecord(d.id, d.data()))
        .sort((a, b) => (b.uploadedAt ?? 0) - (a.uploadedAt ?? 0));
    },

    /**
     * The rules collection in one read: the rules learned from what was
     * posted and the ones the owner wrote share it, so pages showing both
     * read it once rather than twice.
     */
    listRules: async (profileId: string): Promise<{ learned: BankRule[]; custom: CustomBankRule[] }> => {
      const snap = await getDocs(section(profileId, BANK_COLLECTIONS.rules));
      return {
        learned: snap.docs.filter((d) => !isCustomRuleDoc(d.data())).map((d) => toBankRule(d.id, d.data())),
        custom: snap.docs
          .flatMap((d) => {
            const rule = toCustomBankRule(d.id, d.data());
            return rule ? [rule] : [];
          })
          .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id)),
      };
    },

    /** Just these bank lines, read again after a change touched them (missing ones are left out). */
    getBankLines: async (profileId: string, ids: string[]): Promise<BankLineRecord[]> => {
      const snaps = await Promise.all(ids.map((id) => getDoc(doc(section(profileId, BANK_COLLECTIONS.lines), id))));
      return snaps.flatMap((snap) => {
        const record = snap.exists() ? toBankLineRecord(snap.id, snap.data()) : null;
        return record ? [record] : [];
      });
    },

    /** Just these journals (missing ones are left out). */
    getJournals: async (profileId: string, ids: string[]): Promise<Journal[]> => {
      const snaps = await Promise.all(ids.map((id) => getDoc(doc(section(profileId, ACCOUNTING_COLLECTIONS.journals), id))));
      return snaps.flatMap((snap) => {
        const journal = snap.exists() ? toJournal(snap.id, snap.data()) : null;
        return journal ? [journal] : [];
      });
    },

    /** Every reconciliation, newest first, undone ones included (marked inactive). */
    listReconciliations: async (profileId: string): Promise<ReconciliationRecord[]> => {
      const snap = await getDocs(section(profileId, BANK_COLLECTIONS.reconciliations));
      return snap.docs
        .flatMap((d) => {
          const record = toReconciliationRecord(d.id, d.data());
          return record ? [record] : [];
        })
        .sort((a, b) => (b.reconciledAt ?? 0) - (a.reconciledAt ?? 0));
    },

    listEvents: async (profileId: string): Promise<BooksEvent[]> => {
      const snap = await getDocs(
        query(
          section(profileId, ACCOUNTING_COLLECTIONS.events),
          orderBy("at", "desc"),
          limit(BOOKS_EVENTS_LIMIT),
        ),
      );
      return snap.docs.flatMap((d) => {
        const event = toBooksEvent(d.id, d.data());
        return event ? [event] : [];
      });
    },

    run: (command: BooksCommand) => call(command),

    /** Stores a receipt in these books' folder; the journal records it. */
    uploadReceipt: async (profileId: string, file: File): Promise<JournalDocument> => {
      const safe = file.name.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(-80) || "receipt";
      const stored = await StorageService.putFile({
        path: booksFilePath(profileId, safe, Date.now()),
        file,
        metadata: { contentType: file.type || "application/octet-stream", customMetadata: { originalName: file.name } },
      });
      return { path: stored.path, name: file.name.slice(0, 120), contentType: stored.contentType, size: stored.size };
    },

    receiptUrl: (path: string) => StorageService.urlFor(path),

    /** What only Liam knows about one tax year; blank until he saves it. */
    getTaxYear: async (profileId: string, yearEnd: string): Promise<TaxYearInputs> => {
      const snap = await getDoc(doc(getDb(), PROFILES, profileId, TAX_YEAR_COLLECTION, yearEnd));
      return snap.exists() ? toTaxYear(snap.id, snap.data()) : emptyTaxYear(yearEnd);
    },

    /** The equipment list and the tax tags; empty until saved. */
    getTaxRecords: async (profileId: string): Promise<TaxRecords> => {
      const [equipment, tags] = await Promise.all([
        getDoc(doc(getDb(), PROFILES, profileId, TAX_RECORDS_COLLECTION, EQUIPMENT_DOC)),
        getDoc(doc(getDb(), PROFILES, profileId, TAX_RECORDS_COLLECTION, TAGS_DOC)),
      ]);
      return { equipment: toEquipment(equipment.exists() ? equipment.data() : undefined), tags: toTaxTags(tags.exists() ? tags.data() : undefined) };
    },

    /** Which tax dates are ticked off or moved, by obligation id. */
    getFilings: async (profileId: string): Promise<Record<string, FilingRecord>> => {
      const snap = await getDocs(collection(getDb(), PROFILES, profileId, FILINGS_COLLECTION));
      return Object.fromEntries(snap.docs.map((d) => [d.id, toFiling(d.id, d.data())]));
    },

    /** The company's CIPC details; blank until saved. */
    getCompanyDetails: async (): Promise<CompanyDetails> => {
      const snap = await getDoc(doc(getDb(), PROFILES, COMPANY_PROFILE_ID, CIPC_COLLECTION, CIPC_DETAILS_DOC));
      return snap.exists() ? toCompanyDetails(snap.data()) : emptyCompanyDetails();
    },

    /** Every beneficial ownership declaration, newest first. */
    listBoDeclarations: async (): Promise<BoDeclaration[]> => {
      const snap = await getDocs(collection(getDb(), PROFILES, COMPANY_PROFILE_ID, BO_COLLECTION));
      return snap.docs
        .flatMap((d) => {
          const decl = toBoDeclaration(d.id, d.data());
          return decl ? [decl] : [];
        })
        .sort((a, b) => b.version - a.version);
    },

    /** Every saved tax year, by the year's last day (the calendar needs staff and VAT settings). */
    listTaxYears: async (profileId: string): Promise<Record<string, TaxYearInputs>> => {
      const snap = await getDocs(collection(getDb(), PROFILES, profileId, TAX_YEAR_COLLECTION));
      return Object.fromEntries(snap.docs.map((d) => [d.id, toTaxYear(d.id, d.data())]));
    },

    /** A report on the letterhead, rendered on the server from the same journals. */
    reportPdf: (request: ReportPdfRequest) =>
      FunctionsService.call<ReportPdfRequest, { filename: string; base64: string }>("accountingPdf", request),
  };
};

/** What `accountingPdf` accepts (apps/server/src/features/accounting/accountingValidator.ts). */
export type ReportPdfRequest =
  | { report: "profitAndLoss"; profileId: string; from: string; to: string; compareFrom?: string; compareTo?: string }
  | { report: "balanceSheet"; profileId: string; asAt: string; compareAsAt?: string }
  | { report: "budgetVsActual"; profileId: string; fyStart: string; period: BudgetPeriod; forecast: { accountId: string; cents: number }[] }
  | { report: "agedDebtors"; profileId: string; asAt: string }
  | { report: "agedCreditors"; profileId: string; asAt: string }
  | { report: "trialBalance"; profileId: string; asAt: string }
  | { report: "taxEstimate"; profileId: string; asAt: string }
  | { report: "taxReturn"; profileId: string; yearEnd: string }
  | { report: "annualReturn"; profileId: string; year: number }
  | { report: "beneficialOwnership"; profileId: string; versionId: string }
  | { report: "boDocument"; profileId: string; versionId: string; document: BoDocument }
  | { report: "ledger"; profileId: string; accountId: string; from: string; to: string };

export type AccountingRepo = ReturnType<typeof createAccountingRepo>;

export const accountingRepo = createAccountingRepo(
  () => db,
  (command) => FunctionsService.call(ACCOUNTING_FUNCTION, command),
);
